import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";
import GoalsService from "../../services/GoalsService";

const GoalsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialData, setInitialData] = useState<any>(null);

  const [formData, setFormData] = useState({
    weightLossGoal: "",
    mainReason: "",
    targetWeight: "",
    targetWeightUnit: "",
    goal: "",
    activityLevel: "",
    dietaryPreference: "",
    fastingPreference: "",
    fastingWindow: "",
  });

  // Map API activity level to UI display
  const mapActivityLevelFromAPI = (level: string): string => {
    const mapping: { [key: string]: string } = {
      sedentary: "Sedentary",
      lightly_active: "Lightly active",
      moderately_active: "Moderately active",
      very_active: "Very active",
      extremely_active: "Very active", // Map to closest option
    };
    return mapping[level] || "Moderately active";
  };

  // Map UI activity level to API value
  const mapActivityLevelToAPI = (level: string): string => {
    const mapping: { [key: string]: string } = {
      Sedentary: "sedentary",
      "Lightly active": "lightly_active",
      "Moderately active": "moderately_active",
      "Very active": "very_active",
    };
    return mapping[level] || "moderately_active";
  };

  // Map API diet type to UI display
  const mapDietTypeFromAPI = (diet: string): string => {
    const mapping: { [key: string]: string } = {
      classic: "Classic",
      vegetarian: "Vegetarian",
      vegan: "Vegan",
      pescatarian: "Pescatarian",
      keto: "Low Carb / Keto",
      low_carb: "Low Carb / Keto",
      paleo: "Classic", // Map to closest
      mediterranean: "Classic", // Map to closest
    };
    return mapping[diet] || "Classic";
  };

  // Map UI diet type to API value
  const mapDietTypeToAPI = (diet: string): string => {
    const mapping: { [key: string]: string } = {
      Classic: "classic",
      Vegetarian: "vegetarian",
      Vegan: "vegan",
      Pescatarian: "pescatarian",
      "Low Carb / Keto": "keto",
    };
    return mapping[diet] || "classic";
  };

  // Map API fasting window to UI display
  const mapFastingWindowFromAPI = (window: string): string => {
    const mapping: { [key: string]: string } = {
      // API format with underscores
      "16_8": "16:8 (Fast 16 hrs / Eat 8 hrs)",
      "18_6": "18:6 (Fast 18 hrs / Eat 6 hrs)",
      "14_10": "14:10 (Fast 14 hrs / Eat 10 hrs)",
      "20_4": "My eating window varies",
      // Also handle colon format if it comes
      "16:8": "16:8 (Fast 16 hrs / Eat 8 hrs)",
      "18:6": "18:6 (Fast 18 hrs / Eat 6 hrs)",
      "14:10": "14:10 (Fast 14 hrs / Eat 10 hrs)",
      "20:4": "My eating window varies",
      custom: "My eating window varies",
    };
    return mapping[window] || "16:8 (Fast 16 hrs / Eat 8 hrs)";
  };

  // Map UI fasting window to API value (API expects display format like "14:10")
  const mapFastingWindowToAPI = (window: string): string => {
    const mapping: { [key: string]: string } = {
      "16:8 (Fast 16 hrs / Eat 8 hrs)": "16:8",
      "18:6 (Fast 18 hrs / Eat 6 hrs)": "18:6",
      "14:10 (Fast 14 hrs / Eat 10 hrs)": "14:10",
      "My eating window varies": "custom",
    };
    return mapping[window] || "16:8";
  };

  // Map API intermittent fasting to UI display
  const mapIntermittentFastingFromAPI = (fasting: string): string => {
    const mapping: { [key: string]: string } = {
      yes_fasting_window: "Yes — I follow a fasting window",
      sometimes_not_sure: "Sometimes / Not sure",
      no_throughout_day: "No — I eat throughout the day",
      // Also handle full text versions if they come
      "Yes — I follow a fasting window": "Yes — I follow a fasting window",
      "Sometimes / Not sure": "Sometimes / Not sure",
      "No — I eat throughout the day": "No — I eat throughout the day",
    };
    return mapping[fasting] || "Yes — I follow a fasting window";
  };

  // Map UI intermittent fasting to API value (API expects display format)
  const mapIntermittentFastingToAPI = (fasting: string): string => {
    // API expects the display format, not snake_case
    return fasting || "Yes — I follow a fasting window";
  };

  // Map API weight loss reasons to UI display
  const mapWeightLossReasonsFromAPI = (reason: string): string => {
    const mapping: { [key: string]: string } = {
      health: "General health / feel better",
      diabetes: "To improve blood sugar / manage diabetes",
      doctor: "Doctor recommended it",
      confidence: "To look or feel more confident",
      appearance: "To look or feel more confident",
      medical: "Doctor recommended it",
      other: "Other",
    };
    return mapping[reason] || "General health / feel better";
  };

  // Map UI weight loss reasons to API value
  const mapWeightLossReasonsToAPI = (reason: string): string => {
    const mapping: { [key: string]: string } = {
      "General health / feel better": "health",
      "To improve blood sugar / manage diabetes": "diabetes",
      "Doctor recommended it": "doctor",
      "To look or feel more confident": "confidence",
      Other: "other",
    };
    return mapping[reason] || "health";
  };

  // Load goals data from API
  useEffect(() => {
    const loadGoalsData = async () => {
      try {
        setLoading(true);

        // Load goals data
        const response = await GoalsService.getUserGoals();

        if (response && response.goals) {
          const goals = response.goals;

          // Use weight unit from goals API response instead of separate preferences call
          const weightUnit = goals.target_weight_in || "kg";

          // Get target weight based on preferred unit
          const targetWeight = GoalsService.getTargetWeight(goals, weightUnit);

          const loadedData = {
            weightLossGoal: goals.weight_loss_goal || "no",
            mainReason: mapWeightLossReasonsFromAPI(
              goals.weight_loss_reasons || "health"
            ),
            targetWeight: targetWeight ? Number(targetWeight).toFixed(1) : "",
            targetWeightUnit: weightUnit,
            goal:
              goals.weight_loss_goal === "yes"
                ? "Weight Loss"
                : "Maintain Weight", // Dynamic based on weight loss goal
            activityLevel: mapActivityLevelFromAPI(
              goals.activity_level || "moderately_active"
            ),
            dietaryPreference: mapDietTypeFromAPI(goals.diet_type || "classic"),
            fastingPreference: mapIntermittentFastingFromAPI(
              goals.intermittent_fasting || "yes_fasting_window"
            ),
            fastingWindow: mapFastingWindowFromAPI(
              goals.fasting_window_type || "16_8"
            ),
          };

          setFormData(loadedData);
          setInitialData(loadedData);
        }
      } catch (error) {
        alert("Error loading goals data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadGoalsData();
  }, []);

  const handleSave = async () => {
    // Check if data has changed
    if (JSON.stringify(formData) === JSON.stringify(initialData)) {
      alert("No changes made.");
      return;
    }


    // Prepare API payload with proper mapping (matching curl format)
    const targetWeightValue = parseFloat(formData.targetWeight) || 0;

    const goalsPayload = {
      weight_loss_goal: formData.weightLossGoal,
      weight_loss_reasons: mapWeightLossReasonsToAPI(formData.mainReason),
      target_weight_kg:
        formData.targetWeightUnit === "kg"
          ? targetWeightValue
          : targetWeightValue / 2.205,
      target_weight_lbs:
        formData.targetWeightUnit === "lbs"
          ? targetWeightValue
          : targetWeightValue * 2.205,
      target_weight_in: formData.targetWeightUnit,
      activity_level: mapActivityLevelToAPI(formData.activityLevel),
      diet_type: mapDietTypeToAPI(formData.dietaryPreference),
      intermittent_fasting: mapIntermittentFastingToAPI(
        formData.fastingPreference
      ),
      fasting_window_type: mapFastingWindowToAPI(formData.fastingWindow),
    };

    try {
      setSaving(true);

      const response = await GoalsService.updateUserGoals(goalsPayload);

      // Also save to localStorage as backup
      localStorage.setItem("userGoals", JSON.stringify(formData));

      alert("Goals updated successfully!");
      navigate("/glucera/profilepage");
    } catch (error) {
      alert(
        "Error saving goals. Please check your network connection and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // Goal is determined dynamically based on weight_loss_goal
  const activityLevels = [
    "Sedentary",
    "Lightly active",
    "Moderately active",
    "Very active",
  ];
  const dietTypes = [
    "Classic",
    "Vegetarian",
    "Vegan",
    "Pescatarian",
    "Low Carb / Keto",
  ];
  const fastingOptions = [
    "Yes — I follow a fasting window",
    "Sometimes / Not sure",
    "No — I eat throughout the day",
  ];
  const fastingWindows = [
    "16:8 (Fast 16 hrs / Eat 8 hrs)",
    "18:6 (Fast 18 hrs / Eat 6 hrs)",
    "14:10 (Fast 14 hrs / Eat 10 hrs)",
    "My eating window varies",
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
              <p className="text-muted-foreground">Loading goals...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-center pt-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/glucera/profilepage")}
                className="absolute left-4 rounded-xl bg-card/80 hover:bg-card"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold text-foreground">
                Goals & Preferences
              </h1>
            </div>

            {/* Weight Loss Goal */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 font-semibold text-foreground">
                Weight Loss Goal
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setFormData({ ...formData, weightLossGoal: "yes" })
                  }
                  className={`flex-1 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all ${formData.weightLossGoal === "yes"
                    ? "border-blue-500 bg-blue-50 text-blue-600"
                    : "border-border bg-card text-foreground"
                    }`}
                >
                  Yes
                </button>
                <button
                  onClick={() =>
                    setFormData({ ...formData, weightLossGoal: "no" })
                  }
                  className={`flex-1 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all ${formData.weightLossGoal === "no"
                    ? "border-blue-500 bg-blue-50 text-blue-600"
                    : "border-border bg-card text-foreground"
                    }`}
                >
                  No
                </button>
                <button
                  onClick={() =>
                    setFormData({ ...formData, weightLossGoal: "not_sure" })
                  }
                  className={`flex-1 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all ${formData.weightLossGoal === "not_sure"
                    ? "border-blue-500 bg-blue-50 text-blue-600"
                    : "border-border bg-card text-foreground"
                    }`}
                >
                  Not Sure
                </button>
              </div>
            </Card>

            {/* What's your main reason - Only show if Yes */}
            {formData.weightLossGoal === "yes" && (
              <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
                <h3 className="mb-4 font-semibold text-foreground">
                  What's your main reason?
                </h3>
                <div className="space-y-2">
                  {[
                    "To improve blood sugar / manage diabetes",
                    "Doctor recommended it",
                    "General health / feel better",
                    "To look or feel more confident",
                    "Other",
                  ].map(reason => (
                    <button
                      key={reason}
                      onClick={() =>
                        setFormData({ ...formData, mainReason: reason })
                      }
                      className={`w-full rounded-lg border-2 px-4 py-3 text-left text-sm transition-all ${formData.mainReason === reason
                        ? "border-blue-500 bg-blue-50 font-medium text-blue-600"
                        : "border-border text-foreground hover:border-blue-300"
                        }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* Target Weight - Only show if Yes */}
            {formData.weightLossGoal === "yes" && (
              <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
                <h3 className="mb-4 font-semibold text-foreground">
                  Target Weight{" "}
                  <span className="text-muted-foreground">
                    ({formData.targetWeightUnit === "kg" ? "Kg" : "Lbs"})
                  </span>
                </h3>
                <div className="space-y-3">
                  <input
                    type="number"
                    placeholder="56"
                    value={formData.targetWeight}
                    onChange={e =>
                      setFormData({ ...formData, targetWeight: e.target.value })
                    }
                    className="w-full rounded-lg border-2 border-border bg-card px-4 py-3 text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </Card>
            )}

            {/* Activity Level */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 font-semibold text-foreground">
                Activity Level
              </h3>
              <div className="space-y-2">
                {activityLevels.map(level => (
                  <button
                    key={level}
                    onClick={() =>
                      setFormData({ ...formData, activityLevel: level })
                    }
                    className={`w-full rounded-xl border-2 p-3 text-left transition-all ${formData.activityLevel === level
                      ? "border-blue-500 bg-blue-50 font-medium text-blue-600"
                      : "border-border text-foreground hover:border-blue-300"
                      }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </Card>

            {/* Diet Type */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 font-semibold text-foreground">Diet Type</h3>
              <div className="space-y-2">
                {dietTypes.map(diet => (
                  <button
                    key={diet}
                    onClick={() =>
                      setFormData({ ...formData, dietaryPreference: diet })
                    }
                    className={`w-full rounded-lg border-2 px-4 py-3 text-left transition-all ${formData.dietaryPreference === diet
                      ? "border-blue-500 bg-blue-50 font-medium text-blue-600"
                      : "border-border text-foreground hover:border-blue-300"
                      }`}
                  >
                    {diet}
                  </button>
                ))}
              </div>
            </Card>

            {/* Fasting Preference */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 font-semibold text-foreground">
                Intermittent Fasting
              </h3>
              <div className="space-y-2">
                {fastingOptions.map(fasting => (
                  <button
                    key={fasting}
                    onClick={() =>
                      setFormData({ ...formData, fastingPreference: fasting })
                    }
                    className={`w-full rounded-lg border-2 px-4 py-3 text-left transition-all ${formData.fastingPreference === fasting
                      ? "border-blue-500 bg-blue-50 font-medium text-blue-600"
                      : "border-border text-foreground hover:border-blue-300"
                      }`}
                  >
                    {fasting}
                  </button>
                ))}
              </div>
            </Card>

            {/* Fasting Window - Only show if Yes */}
            {formData.fastingPreference ===
              "Yes — I follow a fasting window" && (
                <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
                  <h3 className="mb-4 font-semibold text-foreground">
                    Fasting Window
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {fastingWindows.map(window => (
                      <button
                        key={window}
                        onClick={() =>
                          setFormData({ ...formData, fastingWindow: window })
                        }
                        className={`rounded-lg border-2 px-3 py-3 text-center text-sm transition-all ${formData.fastingWindow === window
                          ? "border-blue-500 bg-blue-50 font-medium text-blue-600"
                          : "border-border text-foreground hover:border-blue-300"
                          }`}
                      >
                        {window}
                      </button>
                    ))}
                  </div>
                </Card>
              )}

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving || loading}
              className="h-12 w-full rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default GoalsPage;
